import { useState, useRef, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useBillingEnabled } from '@/hooks/useBillingEnabled'
import { planApi } from '@/api/planApi'
import { workspaceApi } from '@/api/workspaceApi'
import { redirectToCheckout } from '@/utils/checkout'
import type { Plan } from '@/api/types'
import { SiteShell } from '@/components/layout/SiteShell'
import { PlanSelector } from '@/components/billing/PlanSelector'
import { BillingDisabledBanner } from '@/components/billing/BillingDisabledBanner'
import { toSlug, slugValidationMessage } from '@/utils/slug'
import { ApiError } from '@/api/httpClient'
import { WorkspaceRoleValue } from '@/api/types'

export default function CreateWorkspacePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const memberships = useAuthStore((s) => s.memberships)
  const loadMe = useAuthStore((s) => s.loadMe)
  const createWorkspace = useWorkspaceStore((s) => s.createWorkspace)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const billingEnabled = useBillingEnabled()
  const isSystemAdmin = useAuthStore((s) => s.user?.is_superuser === true)

  const hasFreePlan = workspaces.some((w) => {
    const code = w.subscription_summary?.plan_code ?? 'free'
    const m = memberships.find((mb) => mb.workspace_id === w.id)
    return code === 'free' && m?.role === WorkspaceRoleValue.OWNER
  })

  /**
   * Escolher um plano pago aqui é permitido: o workspace nasce no free (a API recusa
   * plano pago na criação com PAID_PLAN_REQUIRES_CHECKOUT, e com razão — seria plano
   * pago sem pagamento) e nós emendamos no checkout logo em seguida.
   *
   * Antes todo plano pago ficava inerte com "contrate depois". Combinado com o limite
   * de um workspace free por conta, quem já tinha um free chegava numa tela de
   * seleção onde NENHUMA opção era clicável.
   *
   * Admin do sistema segue podendo atribuir plano pago direto (comped), sem checkout.
   */
  function planDisabledReason(plan: Plan): string | null {
    const isFree = plan.code.toLowerCase() === 'free'
    if (isSystemAdmin) return null
    if (isFree) {
      return hasFreePlan
        ? t('workspace.create.freeWorkspaceAlreadyExists', 'Você já tem um workspace free')
        : null
    }
    if (plan.is_available_for_purchase === false) {
      return t('billing.disabled.action', 'Em breve')
    }
    return billingEnabled ? null : t('billing.disabled.action', 'Em breve')
  }

  /**
   * Primeiro workspace da conta: nao mostramos o seletor de planos. O usuario acabou
   * de se cadastrar e cair numa tela de precos antes de ver o produto so cria atrito —
   * criamos direto no free (o backend ja usa free quando `plan_code` vem ausente) e o
   * upgrade fica para depois, pela tela de assinatura. Admin do sistema segue vendo o
   * seletor porque pode "comped" um plano pago na criacao.
   */
  const isFirstWorkspace = memberships.length === 0 && !isSystemAdmin

  const [step, setStep] = useState<'plan' | 'details'>('details')
  const [plans, setPlans] = useState<Plan[]>([])
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null)

  useEffect(() => {
    if (step !== 'plan') return
    setLoadingPlans(true)
    planApi.list().then(setPlans).finally(() => setLoadingPlans(false))
  }, [step])

  const defaultName = t('workspace.create.defaultName', 'Meu workspace')
  const [name, setName] = useState(defaultName)
  const [slug, setSlug] = useState(toSlug(defaultName))
  const slugManualRef = useRef(false)
  const slugInputRef = useRef<HTMLInputElement | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [submittingPlanCode, setSubmittingPlanCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [freePlanBlocked, setFreePlanBlocked] = useState(false)
  const selectablePlans =
    hasFreePlan && !isSystemAdmin
      ? plans.filter((plan) => plan.code.toLowerCase() !== 'free')
      : plans

  function handleNameChange(value: string) {
    setName(value)
    if (!slugManualRef.current) {
      setSlug(toSlug(value))
      setSlugError(null)
    }
  }

  function handleSlugChange(value: string) {
    slugManualRef.current = true
    const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setSlug(normalized)
    setSlugError(slugValidationMessage(normalized))
  }

  function validateDetails(): { name: string; slug: string } | null {
    const trimmedName = name.trim() || defaultName
    const trimmedSlug = slug.trim()

    const slugMsg = slugValidationMessage(trimmedSlug)
    if (slugMsg) {
      setSlugError(slugMsg)
      return null
    }

    setSlugError(null)
    setError(null)
    return { name: trimmedName, slug: trimmedSlug }
  }

  function handleContinueToPlan(e: FormEvent) {
    e.preventDefault()
    if (!validateDetails()) return
    if (isFirstWorkspace) {
      void handleCreateWorkspace(null)
      return
    }
    setStep('plan')
  }

  async function handleCreateWorkspace(planCodeOverride?: string | null) {
    const details = validateDetails()
    if (!details) {
      setStep('details')
      return
    }

    setSubmitting(true)
    setSubmittingPlanCode(planCodeOverride ?? selectedPlanCode)
    setError(null)

    const chosenPlanCode = planCodeOverride ?? selectedPlanCode
    const chosenPlan = plans.find((p) => p.code === chosenPlanCode)
    const wantsPaidCheckout =
      !isSystemAdmin && chosenPlan != null && (chosenPlan.price_cents ?? 0) > 0

    if (wantsPaidCheckout && chosenPlan?.is_available_for_purchase === false) {
      setError(t('billing.planUnavailable', 'Este plano estará disponível em breve.'))
      setSubmitting(false)
      setSubmittingPlanCode(null)
      return
    }

    try {
      // O workspace pago só nasce quando o webhook confirmar o checkout. Criá-lo
      // primeiro como Free conflita com o limite de um Free por proprietário.
      if (wantsPaidCheckout) {
        const result = await workspaceApi.createPaidCheckout(
          {
            name: details.name,
            slug: details.slug,
            plan_code: chosenPlan.code,
          },
          { silent: true },
        )
        redirectToCheckout(result.checkout_url)
        return
      }

      const workspace = await createWorkspace({
        name: details.name,
        slug: details.slug,
        ...(chosenPlanCode ? { plan_code: chosenPlanCode } : {}),
      })
      await loadMe()
      navigate(`/workspace/${details.slug}/projects`, { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'WORKSPACE_SLUG_ALREADY_EXISTS') {
        setSlugError(t('workspace.create.slugTaken', 'Este slug já está em uso. Escolha outro.'))
        slugInputRef.current?.focus()
        setStep('details')
        return
      }
      if (err instanceof ApiError && err.code === 'FREE_PLAN_ALREADY_ACTIVE') {
        setFreePlanBlocked(true)
        return
      }
      if (err instanceof ApiError && err.code === 'PAID_PLAN_REQUIRES_CHECKOUT') {
        setError(
          t(
            'workspace.create.paidPlanRequiresCheckout',
            'Planos pagos não podem ser atribuídos na criação. Crie o workspace no plano gratuito e contrate o plano pago depois.',
          ),
        )
        setStep('plan')
        return
      }
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
      setSubmittingPlanCode(null)
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 12px',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, opacity: 0.8 }
  const helperStyle: React.CSSProperties = { fontSize: 11, opacity: 0.5, marginTop: 4 }
  const errorStyle: React.CSSProperties = { fontSize: 11, color: 'var(--color-danger)', marginTop: 4 }

  // ── free plan blocked (fallback) ──────────────────────────────────────────

  if (freePlanBlocked) {
    return (
      <SiteShell>
        <div style={pageWrapStyle}>
          <div style={cardStyle}>
            <div style={{ fontSize: 36, textAlign: 'center' }}>🚫</div>
            <h2 style={{ margin: 0, fontSize: 20, textAlign: 'center' }}>
              {t('workspace.create.freePlanBlockedTitle', 'Você já tem um workspace gratuito')}
            </h2>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.75, lineHeight: 1.6, textAlign: 'center' }}>
              {billingEnabled
                ? t(
                    'workspace.create.freePlanBlockedDesc',
                    'Cada conta pode ter apenas um workspace no plano free. Para criar um novo workspace, escolha um plano pago.',
                  )
                : t(
                    'workspace.create.freePlanBlockedDescBillingOff',
                    'Cada conta pode ter apenas um workspace no plano free. A contratação de planos pagos ainda está em desenvolvimento.',
                  )}
            </p>
            {billingEnabled ? (
              <button
                onClick={() => { setFreePlanBlocked(false); setStep('plan') }}
                style={primaryBtnStyle}
              >
                {t('workspace.create.choosePaidPlan', 'Escolher plano pago')}
              </button>
            ) : null}
            <button onClick={() => navigate('/workspaces')} style={ghostBtnStyle}>
              {t('workspace.create.freePlanBlockedBack', 'Voltar para meus workspaces')}
            </button>
          </div>
        </div>
      </SiteShell>
    )
  }

  // ── plan step ─────────────────────────────────────────────────────────────

  if (step === 'plan') {
    return (
      <SiteShell>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            background: 'var(--bg-app)',
            color: 'var(--text)',
            fontFamily: 'system-ui, sans-serif',
            padding: '48px 24px 64px',
          }}
        >
          {/* O erro tambem precisa aparecer AQUI: recusas do backend na submissao
              (PAID_PLAN_REQUIRES_CHECKOUT, por exemplo) acontecem neste passo, e
              sem este bloco o clique nao produzia retorno visivel nenhum. */}
          {error ? (
            <div
              role="alert"
              style={{
                maxWidth: 980,
                margin: '0 auto 24px',
                background: 'var(--color-danger-bg)',
                border: '1px solid var(--color-danger-border)',
                color: 'var(--color-danger)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                lineHeight: 1.5,
                boxSizing: 'border-box',
              }}
            >
              {error}
            </div>
          ) : null}

          {!billingEnabled && !isSystemAdmin ? (
            <div style={{ marginBottom: 28 }}>
              <BillingDisabledBanner />
            </div>
          ) : null}

          {hasFreePlan && !isSystemAdmin ? (
            <div
              role="status"
              style={{
                maxWidth: 980,
                margin: '0 auto 24px',
                background: 'color-mix(in srgb, var(--accent) 10%, var(--bg-card))',
                border: '1px solid color-mix(in srgb, var(--accent) 38%, var(--border))',
                color: 'var(--text)',
                borderRadius: 10,
                padding: '12px 16px',
                fontSize: 13,
                lineHeight: 1.55,
                boxSizing: 'border-box',
              }}
            >
              {t(
                'workspace.create.paidPlanRequiredNotice',
                'Você já possui um workspace gratuito. Para criar outro workspace, escolha um plano pago.',
              )}
            </div>
          ) : null}

          <PlanSelector
            plans={selectablePlans}
            loading={loadingPlans}
            selectedPlanCode={selectedPlanCode}
            onSelect={(plan) => {
              if (planDisabledReason(plan)) return
              setSelectedPlanCode(plan.code)
            }}
            title={t('workspace.create.choosePlanTitle', 'Escolha o plano ideal')}
            subtitle={t(
              'workspace.create.choosePlanSubtitle',
              'Selecione o plano para o novo workspace.',
            )}
            note={
              isSystemAdmin
                ? t(
                    'workspace.create.adminComp',
                    'Como admin do sistema você pode criar em qualquer plano, sem cobrança.',
                  )
                : undefined
            }
            renderAction={(plan, state) => {
              const disabledReason = planDisabledReason(plan)
              const isDisabled = disabledReason !== null
              return (
                <button
                  disabled={isDisabled || submitting}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isDisabled || submitting) return
                    setSelectedPlanCode(plan.code)
                    void handleCreateWorkspace(plan.code)
                  }}
                  style={{
                    width: '100%',
                    minHeight: 52,
                    borderRadius: 14,
                    border: isDisabled
                      ? '1px solid var(--border)'
                      : state.isSelected
                        ? '1px solid rgba(34,211,238,.58)'
                        : 'transparent',
                    background: isDisabled
                      ? 'color-mix(in srgb, var(--bg-card) 92%, var(--bg-app))'
                      : 'linear-gradient(90deg, #A855F7 0%, #22D3EE 100%)',
                    color: isDisabled ? 'var(--text)' : '#F8FBFF',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: isDisabled || submitting ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.72 : submitting ? 0.7 : 1,
                  }}
                >
                  {isDisabled
                    ? disabledReason
                    : submitting && submittingPlanCode === plan.code
                      ? t('workspace.create.submitting', 'Criando...')
                      : !isSystemAdmin && (plan.price_cents ?? 0) > 0
                        ? t('workspace.create.submitPaid', 'Criar e assinar')
                        : t('workspace.create.submit', 'Criar workspace')}
                </button>
              )
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
            <button onClick={() => setStep('details')} style={ghostBtnStyle}>
              {t('workspace.create.backToDetails', '← Voltar aos detalhes')}
            </button>
          </div>
        </div>
      </SiteShell>
    )
  }

  // ── details step ──────────────────────────────────────────────────────────

  return (
    <SiteShell>
      <div style={pageWrapStyle}>
        <div style={cardStyle}>
          <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h1 style={{ margin: 0, fontSize: 26 }}>
              {memberships.length === 0
                ? t('workspace.create.title', 'Crie seu workspace')
                : t('workspace.create.titleNew', 'Novo workspace')}
            </h1>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.7 }}>
              {memberships.length === 0
                ? isFirstWorkspace
                  ? t(
                      'workspace.create.subtitleOnboardingFree',
                      'Configure seu workspace para começar a usar o MorphERD. Você começa no plano gratuito.',
                    )
                  : t('workspace.create.subtitleOnboarding', 'Configure seu workspace para começar a usar o MorphERD.')
                : t('workspace.create.subtitle', 'Um workspace agrupa seus projetos e membros de equipe.')}
            </p>
          </header>

          <form onSubmit={handleContinueToPlan} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error ? (
              <div
                style={{
                  background: 'var(--color-danger-bg)',
                  border: '1px solid var(--color-danger-border)',
                  color: 'var(--color-danger)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={labelStyle} htmlFor="ws-name">
                {t('workspace.create.nameLabel', 'Nome do workspace')}
              </label>
              <input
                id="ws-name"
                required
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                maxLength={100}
                placeholder={t('workspace.create.namePlaceholder', 'Meu workspace')}
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={labelStyle} htmlFor="ws-slug">
                {t('workspace.create.slugLabel', 'Slug do workspace')}
              </label>
              <input
                ref={slugInputRef}
                id="ws-slug"
                required
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                maxLength={120}
                placeholder={t('workspace.create.slugInputPlaceholder')}
                style={{
                  ...inputStyle,
                  borderColor: slugError ? 'var(--color-danger-border)' : 'var(--border)',
                }}
              />
              {slugError ? (
                <span style={errorStyle}>{slugError}</span>
              ) : (
                <span style={helperStyle}>
                  {t('workspace.create.slugHelper', 'Esse slug será usado na URL do seu workspace.')}
                </span>
              )}
              {slug && !slugError ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 6,
                    padding: '6px 10px',
                    background: 'color-mix(in srgb, var(--bg-card) 88%, var(--bg-app))',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                  }}
                >
                  <span style={{ fontSize: 11, opacity: 0.4 }}>URL:</span>
                  <code style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'monospace' }}>
                    /workspace/{slug}
                  </code>
                </div>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={submitting || !!slugError}
              style={{
                ...primaryBtnStyle,
                opacity: slugError || submitting ? 0.7 : 1,
                cursor: slugError || submitting ? 'not-allowed' : 'pointer',
                marginTop: 4,
              }}
            >
              {submitting
                ? t('workspace.create.submitting', 'Criando...')
                : isFirstWorkspace
                  ? t('workspace.create.submit', 'Criar workspace')
                  : t('workspace.create.continueToPlan', 'Continuar para escolher o plano')}
            </button>
          </form>

          {memberships.length > 0 ? (
            <button onClick={() => navigate(-1)} style={ghostBtnStyle}>
              {t('common.cancel', 'Cancelar')}
            </button>
          ) : null}
        </div>
      </div>
    </SiteShell>
  )
}

// ── shared styles ─────────────────────────────────────────────────────────────

const pageWrapStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-app)',
  color: 'var(--text)',
  fontFamily: 'system-ui, sans-serif',
  padding: 24,
}

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 40,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
}

const primaryBtnStyle: React.CSSProperties = {
  background: '#3b82f6',
  border: 'none',
  color: 'white',
  borderRadius: 8,
  padding: '11px 12px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
}

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
  textAlign: 'center' as const,
}
